import React, { createContext, useContext, useState, ReactNode } from "react";

export interface FailedTile {
  id: string;
  text: string;
  reason: string;
}

interface BulkWriteItProgressContextType {
  isOpen: boolean;
  progress: number;
  statusMessage: string;
  failedTiles: FailedTile[];
  showProgress: () => void;
  hideProgress: () => void;
  updateProgress: (progress: number, message: string, failedTiles: FailedTile[]) => void;
  completeProgress: (finalFailedTiles: FailedTile[]) => void;
  resetProgress: () => void;
}

const BulkWriteItProgressContext = createContext<BulkWriteItProgressContextType | undefined>(
  undefined
);

export const BulkWriteItProgressProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [failedTiles, setFailedTiles] = useState<FailedTile[]>([]);

  const showProgress = () => setIsOpen(true);
  const hideProgress = () => {
    setIsOpen(false);
    setFailedTiles([]); // Clear failed tiles when hiding progress normally
  };
  const updateProgress = (
    newProgress: number,
    newMessage: string,
    newFailedTiles: FailedTile[]
  ) => {
    setProgress(newProgress);
    setStatusMessage(newMessage);
    setFailedTiles(newFailedTiles);
    setIsOpen(true);
  };

  const completeProgress = (finalFailedTiles: FailedTile[]) => {
    setProgress(100);
    setFailedTiles(finalFailedTiles);
    setIsOpen(true);
  };

  const resetProgress = () => {
    setIsOpen(false);
    setProgress(0);
    setStatusMessage("");
    setFailedTiles([]);
  };

  return (
    <BulkWriteItProgressContext.Provider
      value={{
        isOpen,
        progress,
        statusMessage,
        failedTiles,
        showProgress,
        hideProgress,
        updateProgress,
        completeProgress,
        resetProgress,
      }}
    >
      {children}
    </BulkWriteItProgressContext.Provider>
  );
};

export const useBulkWriteItProgress = () => {
  const context = useContext(BulkWriteItProgressContext);
  if (context === undefined) {
    throw new Error("useBulkWriteItProgress must be used within a BulkWriteItProgressProvider");
  }
  return context;
};
